import React, { useState, useEffect } from 'react';
import { Button, Alert, Form, Row, Col, Spinner, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { getBankInfo } from '../../../utils/bankAccountUtils';
import Select from 'react-select';
import { brandSelectTheme } from '../Common/reactSelectTheme';
import ConfirmModal from '../Common/ConfirmModal';
import PageHeader from '../../layouts/PageHeader';
import UnifiedBankAccounts from './UnifiedBankAccounts';
import { Plus, AlertCircle, CheckCircle, Loader2, Landmark } from 'lucide-react';

/**
 * The company's bank accounts: which account in the kontoplan each one books
 * on. Lives in Inställningar → Bankkonton (`embedded`, no page header, with
 * the bank connections below it); the old /bank-accounts page redirects here.
 * Saving a mapping also applies it to the bank rows already imported, so
 * there is no separate "apply" step for the user.
 */
const BankAccountMappings = ({ embedded = false }) => {
  const { companyId } = useParams();

  const [mappings, setMappings] = useState([]);
  const [unmappedAccounts, setUnmappedAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]); // Chart of accounts (payment accounts)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [selectedUnmapped, setSelectedUnmapped] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // mapping pending confirmation

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch in parallel
      const [mappingsRes, unmappedRes, accountsRes] = await Promise.all([
        axios.get(getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings/unmapped`), { withCredentials: true }),
        axios.get(getApiUrl(`${companyId}/accounting/accounts`), { withCredentials: true })
      ]);

      setMappings(mappingsRes.data.mappings || []);
      setUnmappedAccounts(unmappedRes.data.unmappedAccounts || []);

      // Filter to only payment accounts
      const paymentAccounts = (accountsRes.data.accounts || []).filter(acc => acc.is_payment_account === 1);
      setAccounts(paymentAccounts);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Kunde inte hämta kontodata. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMapping = (unmapped = null) => {
    setSelectedUnmapped(unmapped);
    setShowCreateModal(true);
  };

  const handleEditMapping = (mapping) => {
    setSelectedMapping(mapping);
    setShowEditModal(true);
  };

  // Deletion is confirmed in-app (styled modal), never a browser alert box.
  const handleDeleteMapping = (mappingId) => {
    setDeleteTarget(mappings.find((m) => m.mapping_id === mappingId) || { mapping_id: mappingId });
  };

  const confirmDeleteMapping = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings/${deleteTarget.mapping_id}`),
        { withCredentials: true }
      );
      setDeleteTarget(null);
      setSuccess('Kopplingen är borttagen.');
      fetchData();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      setError('Kunde inte ta bort kopplingen.');
      throw err; // keep the modal open so the alert is visible
    }
  };

  // Stamp the mappings on the bank rows already imported. Runs after every
  // save; the user never has to ask for it.
  const applyMappings = async () => {
    try {
      const res = await axios.post(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/apply-mappings`),
        {},
        { withCredentials: true }
      );
      return Number(res.data?.updated) || 0;
    } catch (err) {
      console.error('Error applying mappings:', err);
      return null;
    }
  };

  const afterSave = async (message) => {
    const updated = await applyMappings();
    setSuccess(updated ? `${message} ${updated} bankhändelser bokförs nu mot kontot.` : message);
    fetchData();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="col-12">
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <AlertCircle size={16} className="me-2" />
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            <CheckCircle size={16} className="me-2" />
            {success}
          </Alert>
        )}

        {!embedded && (
          <PageHeader
            icon={Landmark}
            title="Bankkonton"
            subtitle="Vilket konto i kontoplanen varje bankkonto bokförs på."
          />
        )}

        <UnifiedBankAccounts
          companyId={companyId}
          mappings={mappings}
          unmappedAccounts={unmappedAccounts}
          onCreateMapping={handleCreateMapping}
          onEditMapping={handleEditMapping}
          onDeleteMapping={handleDeleteMapping}
          onChanged={fetchData}
        />
        {/* A bank account that has not appeared in any import yet. */}
        <div style={{ marginTop: 8 }}>
          <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" onClick={() => handleCreateMapping()}>
            <Plus /> Lägg till bankkonto
          </button>
        </div>
      </div>

      <ConfirmModal
        show={Boolean(deleteTarget)}
        title="Ta bort kopplingen?"
        body="Bankkontot bokförs då på Företagskonto (1930) tills du kopplar det igen. Redan bokförda transaktioner påverkas inte."
        detail={deleteTarget?.accounting_number
          ? `${deleteTarget.clearing_number}-${deleteTarget.account_number} → ${deleteTarget.accounting_number} ${deleteTarget.accounting_name}`
          : null}
        confirmLabel="Ta bort koppling"
        onConfirm={confirmDeleteMapping}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Create/Edit Modals */}
      <MappingFormModal
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false);
          setSelectedUnmapped(null);
        }}
        accounts={accounts}
        unmapped={selectedUnmapped}
        onSuccess={() => {
          setShowCreateModal(false);
          setSelectedUnmapped(null);
          afterSave('Bankkontot är kopplat.');
        }}
        companyId={companyId}
      />

      <EditMappingModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setSelectedMapping(null);
        }}
        mapping={selectedMapping}
        accounts={accounts}
        onSuccess={() => {
          setShowEditModal(false);
          setSelectedMapping(null);
          afterSave('Ändringen är sparad.');
        }}
        companyId={companyId}
      />
    </>
  );
};

/**
 * Create Mapping Modal Component
 */
const MappingFormModal = ({ show, onHide, accounts, unmapped, onSuccess, companyId }) => {
  const [formData, setFormData] = useState({
    clearingNumber: '',
    accountNumber: '',
    bankName: '',
    accountId: '',
    accountNickname: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (unmapped) {
      const derived = getBankInfo(unmapped.clearing_number || '');
      setFormData({
        clearingNumber: unmapped.clearing_number || '',
        accountNumber: unmapped.account_number || '',
        bankName: unmapped.bank_name || (derived ? derived.bank : ''),
        accountId: '',
        accountNickname: ''
      });
    } else {
      setFormData({
        clearingNumber: '',
        accountNumber: '',
        bankName: '',
        accountId: '',
        accountNickname: ''
      });
    }
  }, [unmapped, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings`),
        formData,
        { withCredentials: true }
      );

      onSuccess();
    } catch (err) {
      console.error('Error creating mapping:', err);
      setError(err.response?.data?.message || 'Kunde inte koppla bankkontot');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Koppla bankkonto till kontoplanen</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Clearingnummer *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.clearingNumber}
                  onChange={(e) => {
                    const clearing = e.target.value;
                    const derived = getBankInfo(clearing);
                    // Derive the bank from the clearing number (national
                    // standard ranges) — user only types a bank name when
                    // the range is unknown.
                    setFormData((prev) => ({
                      ...prev,
                      clearingNumber: clearing,
                      bankName: derived ? derived.bank : prev.bankName
                    }));
                  }}
                  required
                  disabled={!!unmapped}
                  placeholder="t.ex. 8327"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Kontonummer *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                  required
                  disabled={!!unmapped}
                  placeholder="t.ex. 123456789"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Banknamn</Form.Label>
            <Form.Control
              type="text"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              placeholder="identifieras från clearingnummer"
            />
            <Form.Text muted>
              {getBankInfo(formData.clearingNumber)
                ? `Identifierad från clearingnummer: ${getBankInfo(formData.clearingNumber).bank}`
                : 'Okänt clearingnummer — ange banknamn manuellt vid behov.'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Bokförs på konto *</Form.Label>
            <Select
              options={accounts.map((acc) => ({
                value: acc.account_id,
                label: `${acc.account_number} - ${acc.account_name}`,
              }))}
              value={
                accounts
                  .filter((acc) => String(acc.account_id) === String(formData.accountId))
                  .map((acc) => ({ value: acc.account_id, label: `${acc.account_number} - ${acc.account_name}` }))[0] || null
              }
              onChange={(opt) => setFormData({ ...formData, accountId: opt ? opt.value : '' })}
              placeholder="Sök kontonummer eller namn…"
              noOptionsMessage={() => 'Inget konto matchar'}
              isSearchable
              isClearable
              theme={brandSelectTheme}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
            <Form.Text className="text-muted">
              Kontoplanens bankkonton (1910–1999). De flesta företag använder 1930 Företagskonto.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Smeknamn (frivilligt)</Form.Label>
            <Form.Control
              type="text"
              value={formData.accountNickname}
              onChange={(e) => setFormData({ ...formData, accountNickname: e.target.value })}
              placeholder="t.ex. Skattekonto, Företagskonto, Sparkonto"
            />
            <Form.Text className="text-muted">
              Ett användarvänligt namn för att identifiera kontot
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Avbryt
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                Sparar...
              </>
            ) : 'Spara'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/**
 * Edit Mapping Modal Component
 */
const EditMappingModal = ({ show, onHide, mapping, accounts, onSuccess, companyId }) => {
  const [formData, setFormData] = useState({
    bankName: '',
    accountId: '',
    accountNickname: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (mapping) {
      setFormData({
        bankName: mapping.bank_name || '',
        accountId: mapping.account_id || '',
        accountNickname: mapping.account_nickname || ''
      });
    }
  }, [mapping]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.put(
        getApiUrl(`${companyId}/accounting/reconciliation/banking/account-mappings/${mapping.mapping_id}`),
        formData,
        { withCredentials: true }
      );

      onSuccess();
    } catch (err) {
      console.error('Error updating mapping:', err);
      setError(err.response?.data?.message || 'Kunde inte spara ändringen');
    } finally {
      setLoading(false);
    }
  };

  if (!mapping) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Ändra bokföringskonto</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          <div className="mb-3 p-3" style={{ backgroundColor: '#f9fafb', borderRadius: '6px' }}>
            <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              Bankkonto (kan inte ändras)
            </div>
            <div style={{ fontSize: '1rem', fontWeight: '600' }}>
              {mapping.clearing_number}-{mapping.account_number}
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Banknamn</Form.Label>
            <Form.Control
              type="text"
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Bokförs på konto *</Form.Label>
            <Select
              options={accounts.map((acc) => ({
                value: acc.account_id,
                label: `${acc.account_number} - ${acc.account_name}`,
              }))}
              value={
                accounts
                  .filter((acc) => String(acc.account_id) === String(formData.accountId))
                  .map((acc) => ({ value: acc.account_id, label: `${acc.account_number} - ${acc.account_name}` }))[0] || null
              }
              onChange={(opt) => setFormData({ ...formData, accountId: opt ? opt.value : '' })}
              placeholder="Sök kontonummer eller namn…"
              noOptionsMessage={() => 'Inget konto matchar'}
              isSearchable
              isClearable
              theme={brandSelectTheme}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Smeknamn</Form.Label>
            <Form.Control
              type="text"
              value={formData.accountNickname}
              onChange={(e) => setFormData({ ...formData, accountNickname: e.target.value })}
              placeholder="t.ex. Skattekonto, Företagskonto"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            Avbryt
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="me-2" style={{ animation: 'spin 1s linear infinite' }} />
                Sparar...
              </>
            ) : 'Spara'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BankAccountMappings;
